import { showToast } from '@/helper/toast.helper';
import { buildQueryParams } from '@/helper/utils';
import {
  AddUserToProjectResponseDto,
  AllProjectsResponseDto,
  CreateProjectDto,
  CreateProjectResponseDto,
  GetProjectByIdResponseDto,
  GetProjectsDropDownDto,
  Project,
  ProjectFilters,
  UpdateProjectDto,
  UpdateProjectResponseDto,
} from '@/types/project.types';
import api from '../instance';

export const API_URL = `/api/v1`;

export const projectsService = {
  createProjects: async (data: CreateProjectDto): Promise<CreateProjectResponseDto> => {
    try {
      const res = await api.projectApi.post<CreateProjectResponseDto>(`/projects`, data, {
        headers: {
          'x-show-loader': 'true',
          'X-Mm-Service': 'mm-content-mgmt-svc',
        },
      });
      return res.data;
    } catch (error: unknown) {
      if (error instanceof Error) throw error;
      throw new Error('Error creating project');
    }
  },
  getProjectsList: async (filters?: ProjectFilters) => {
    const params = filters ? buildQueryParams(filters) : {};
    try {
      const res = await api.projectApi.get<AllProjectsResponseDto>(`/projects`, {
        params,
        headers: {
          'x-show-loader': 'true',
          'X-Mm-Service': 'mm-content-mgmt-svc',
        },
      });

      const roleInfoStr = localStorage.getItem('roleInfo');
      const roleName = roleInfoStr ? JSON.parse(roleInfoStr).name : undefined;

      if (roleName === 'SUPER_ADMIN' || roleName === 'ADMIN') return res.data;
      const projectRoles: Record<string, string> = {};
      res.data.data.forEach((project) => {
        projectRoles[project.id] = project.roleInfo.name;
      });
      localStorage.setItem('projectRoles', JSON.stringify(projectRoles));
      return res.data;
    } catch (error: unknown) {
      if (error instanceof Error) throw error;
      throw new Error('Error fetching projects');
    }
  },
  getProjectsById: async (id: string) => {
    try {
      const res = await api.projectApi.get<GetProjectByIdResponseDto>(`/projects/${id}`, {
        headers: {
          'x-show-loader': 'true',
          'X-Mm-Service': 'mm-content-mgmt-svc',
        },
      });
      return res.data;
    } catch (error: unknown) {
      if (error instanceof Error) throw error;
      throw new Error('Error fetching project by ID');
    }
  },
  // TODO: Update projects
  // getting data.project, instead we should get data: {...project}
  updateProjects: async (id: string, data: UpdateProjectDto): Promise<Project> => {
    try {
      const res = await api.projectApi.put<UpdateProjectResponseDto>(`/projects/${id}`, data, {
        headers: {
          'x-show-loader': 'true',
          'X-Mm-Service': 'mm-content-mgmt-svc',
        },
      });
      return res.data.data;
    } catch (error: unknown) {
      if (error instanceof Error) throw error;
      throw new Error('Error updating project');
    }
  },

  // TODO: Fix the reponse Dto of delete Project
  // getting data.data = project, instead we should get data: {...project}
  deleteProjects: async (id: string, deletedBy: string) => {
    try {
      const res = await api.projectApi.delete(`/projects/${id}`, {
        params: { deletedBy },
        headers: {
          'X-Mm-Service': 'mm-content-mgmt-svc',
          'x-show-loader': 'true',
        },
      });
      return res.data;
    } catch (error: unknown) {
      if (error instanceof Error) throw error;
      throw new Error('Error deleting project');
    }
  },

  // TODO: Ask the backend team to make this common to /projects
  getProjectsDropdown: async () => {
    try {
      const res = await api.projectApi.get<GetProjectsDropDownDto>('/sites/project/dropdown', {
        headers: {
          'X-Mm-Service': 'mm-content-mgmt-svc',
          'x-show-loader': 'true',
        },
      });
      return res.data;
    } catch (error: unknown) {
      if (error instanceof Error) throw error;
      throw new Error('Error get projects drowndown');
    }
  },
  exportProjects: async () => {
    try {
      const res = await api.projectApi.get('/projects/bulk-export', {
        responseType: 'blob',
      });
      return res.data;
    } catch (error) {
      if (error instanceof Error) throw error;
      throw new Error('Error exporting projects');
    }
  },
  addUserToProject: async (
    projectId: number,
    phoneNumber: string,
    blueNumberId: string,
    userId: number,
  ) => {
    try {
      const res = await api.projectApi.post<AddUserToProjectResponseDto>('/projects/user-consent', {
        projectId,
        phoneNumber,
        blueNumberId,
        userId,
      });

      return res.data;
    } catch (error) {
      if (error instanceof Error) throw error;
      throw new Error('Error adding user to project');
    }
  },
  verifyMobileUser: async (phoneNumber: string, countryCode: string, blueNumberId: string) => {
    try {
      const res = await api.userApi.get('/mobile-user/by-mobile', {
        params: { phoneNumber, countryCode, blueNumberId },
      });
      return res.data;
    } catch (error) {
      if (error instanceof Error) throw error;
      throw new Error('Error verifying mobile user');
    }
  },
  getProjectUsers: async (projectId: number) => {
    try {
      const res = await api.projectApi.get(`/projects/consent-users/by-project`, {
        params: { projectId },
        headers: {
          'x-show-loader': 'true',
          'X-Mm-Service': 'mm-content-mgmt-svc',
        },
      });

      return res.data;
    } catch (error: any) {
      const message =
        error?.response?.data?.message || error?.message || 'Error fetching project users';

      showToast.error(message);
      throw new Error(message);
    }
  },

  toggleConsentUserStatus: async (projectId: number, userId: number, status: boolean) => {
    const res = await api.projectApi.put(
      '/projects/consent-users/toggle-status',
      {
        projectId,
        userId,
        status,
      },
      {
        headers: {
          'x-show-loader': 'true',
          'X-Mm-Service': 'mm-content-mgmt-svc',
        },
      },
    );

    return res.data;
  },

  deleteProjectConsentUser: async (projectId: number, userId: number) => {
    try {
      const res = await api.projectApi.delete('/projects/consent-users', {
        data: {
          projectId,
          userId,
        },
        headers: {
          'x-show-loader': 'true',
          'X-Mm-Service': 'mm-content-mgmt-svc',
        },
      });

      return res.data;
    } catch (error: any) {
      const message =
        error?.response?.data?.message || error?.message || 'Error deleting project consent user';

      showToast.error(message);
      throw new Error(message);
    }
  },

  exportProjectConsentUsers: async (projectId: number) => {
    try {
      const res = await api.projectApi.get(`/projects/export/consent-users/by-project`, {
        params: { projectId },
        responseType: 'blob',
        headers: {
          'x-show-loader': 'true',
          'X-Mm-Service': 'mm-content-mgmt-svc',
        },
      });

      return res.data;
    } catch (error: any) {
      const message =
        error?.response?.data?.message || error?.message || 'Error exporting consent user list';

      showToast.error(message);
      throw new Error(message);
    }
  },
};
